import { create } from "zustand";
import type { QuadrantPoint } from "../types/quadrant";
import {
  quadrantApi,
  type QuadrantPoint as ApiQuadrantPoint,
} from "../services/api";

interface QuadrantFilters {
  interval: string;
  minScore: number;
  showNeutral: boolean;
  symbolFilter: string;
}

interface QuadrantStore {
  points: QuadrantPoint[];
  selectedPoint: QuadrantPoint | null;
  drawerOpen: boolean;
  loading: boolean;
  error: string | null;
  lastUpdate: Date | null;
  filters: QuadrantFilters;
  filteredPointsCache: {
    key: string;
    points: QuadrantPoint[];
  } | null;

  setPoints: (points: QuadrantPoint[]) => void;
  selectPoint: (point: QuadrantPoint | null) => void;
  openDrawer: (point: QuadrantPoint) => void;
  closeDrawer: () => void;
  refreshData: () => Promise<void>;
  setFilter: (
    key: keyof QuadrantFilters,
    value: string | number | boolean,
  ) => void;
  getFilteredPoints: () => QuadrantPoint[];
  invalidateCache: () => void;
}

function mapApiToPoint(apiPoint: ApiQuadrantPoint): QuadrantPoint {
  return {
    id: apiPoint.id,
    symbol: apiPoint.symbol,
    interval: apiPoint.interval,
    rsi: apiPoint.rsi,
    score: apiPoint.score,
    zone: apiPoint.zone as QuadrantPoint["zone"],
    price: apiPoint.price,
    change24h: apiPoint.change24h,
    volume24h: apiPoint.volume24h,
    divergenceStrength: apiPoint.divergenceStrength,
    detectedAt: apiPoint.detectedAt,
    lastUpdate: apiPoint.lastUpdate,
  };
}

let refreshSequence = 0;

export const useQuadrantStore = create<QuadrantStore>((set, get) => ({
  points: [],
  selectedPoint: null,
  drawerOpen: false,
  loading: false,
  error: null,
  lastUpdate: null,
  filters: {
    interval: "ALL",
    minScore: 0,
    showNeutral: true,
    symbolFilter: "",
  },
  filteredPointsCache: null,

  setPoints: (points) =>
    set({ points, lastUpdate: new Date(), filteredPointsCache: null }),

  selectPoint: (point) => set({ selectedPoint: point }),

  openDrawer: (point) => set({ selectedPoint: point, drawerOpen: true }),

  closeDrawer: () => set({ drawerOpen: false }),

  refreshData: async () => {
    const { filters } = get();
    const requestId = ++refreshSequence;
    set({ loading: true, error: null });

    try {
      const apiPoints = await quadrantApi.getPoints({
        interval: filters.interval === "ALL" ? undefined : filters.interval,
        minScore: filters.minScore,
        showNeutral: filters.showNeutral,
      });

      if (requestId !== refreshSequence) {
        return;
      }

      const points = apiPoints.map(mapApiToPoint);
      set({
        points,
        loading: false,
        error: null,
        lastUpdate: new Date(),
        filteredPointsCache: null,
      });
    } catch (error) {
      if (requestId !== refreshSequence) {
        return;
      }

      if (import.meta.env.DEV) {
        console.error("Failed to fetch data from API:", error);
      }

      const { default: i18n } = await import("../i18n");
      set((state) => ({
        points: state.points,
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : i18n.t("common.error.network"),
        lastUpdate: state.lastUpdate ?? new Date(),
      }));
    }
  },

  setFilter: (key, value) => {
    set((state) => ({
      filters: {
        ...state.filters,
        [key]: value,
      },
      filteredPointsCache: null,
    }));
  },

  invalidateCache: () => set({ filteredPointsCache: null }),

  getFilteredPoints: () => {
    const { points, filters, filteredPointsCache } = get();
    const cacheKey = `${filters.interval}-${filters.minScore}-${filters.showNeutral}-${filters.symbolFilter}`;

    if (filteredPointsCache && filteredPointsCache.key === cacheKey) {
      return filteredPointsCache.points;
    }

    let filtered = [...points];

    if (filters.interval !== "ALL") {
      filtered = filtered.filter((p) => p.interval === filters.interval);
    }

    if (filters.minScore > 0) {
      filtered = filtered.filter((p) => Math.abs(p.score) >= filters.minScore);
    }

    if (!filters.showNeutral) {
      filtered = filtered.filter((p) => p.zone !== "NEUTRAL");
    }

    if (filters.symbolFilter.trim()) {
      const query = filters.symbolFilter.toLowerCase().trim();
      filtered = filtered.filter((p) => p.symbol.toLowerCase().includes(query));
    }

    queueMicrotask(() => {
      const currentCache = get().filteredPointsCache;
      if (!currentCache || currentCache.key !== cacheKey) {
        set({ filteredPointsCache: { key: cacheKey, points: filtered } });
      }
    });

    return filtered;
  },
}));
